// frontend/src/components/editors/IndicatorEditorRSI.tsx
import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from 'react';
import { useSeriesOptions } from '../../utils/useSeriesOptions';
import { InstrumentPill, ValuePill, FromIndicatorBadge } from './IndicatorPreviewFields';

export type IndicatorEditorRSIRef = {
  getConfig: () => {
    instrumentId: string;
    field: string;
    period: number;
    lag: number;
  };
};

type Props = {
  config: {
    instrumentId: string;
    field?: string;
    period?: number;
    lag?: number;
    lookback?: number; // legacy; treated as period when period not set
  };
  onChange: (config: Props['config']) => void;
  editMode: boolean;
  indicatorId?: string;
};

const IndicatorEditorRSI = forwardRef<IndicatorEditorRSIRef, Props>(
  ({ config, onChange, editMode, indicatorId }, ref) => {
    const [period, setPeriod] = useState(config.period ?? config.lookback ?? 14);
    const [lag, setLag] = useState(config.lag ?? 0);

    const allSeriesOptions = useSeriesOptions(indicatorId);
    const hasSingleOption = allSeriesOptions.length === 1;

    useEffect(() => {
      if (hasSingleOption && config.instrumentId !== allSeriesOptions[0].id) {
        onChange({ ...config, instrumentId: allSeriesOptions[0].id });
      }
    }, [hasSingleOption, allSeriesOptions, config.instrumentId, onChange]);

    const selectedSeries = allSeriesOptions.find(s => s.id === config.instrumentId);
    const availableFields = selectedSeries
      ? Object.keys(selectedSeries.emittedFields).filter(
        f => selectedSeries.emittedFields[f] === 'Number'
      )
      : [];

    const currentField = config.field && availableFields.includes(config.field)
      ? config.field
      : availableFields.includes('close')
        ? 'close'
        : availableFields[0] || 'Value';

    useEffect(() => {
      if (config.field !== currentField) {
        onChange({ ...config, field: currentField });
      }
    }, [currentField, config, onChange]);

    const handleSeriesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange({ ...config, instrumentId: e.target.value });
    };

    const handlePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      if (!isNaN(val) && val > 0) {
        setPeriod(val);
        onChange({ ...config, period: val });
      }
    };

    const handleLagChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const val = Number(e.target.value);
      if (!isNaN(val) && val >= 0) {
        setLag(val);
        onChange({ ...config, lag: val });
      }
    };

    useImperativeHandle(ref, () => ({
      getConfig: () => ({
        instrumentId: config.instrumentId,
        field: currentField,
        period,
        lag,
      }),
    }));

    if (!editMode) {
      const label = selectedSeries?.label || '???';
      const fieldLabel = currentField.charAt(0).toUpperCase() + currentField.slice(1);
      return (
        <div className="flex flex-wrap gap-2 items-center text-sm">
          <InstrumentPill>{label}</InstrumentPill>
          <ValuePill label="Period" value={period} />
          <ValuePill label="Field" value={fieldLabel} />
          {lag > 0 && <ValuePill label="Lag" value={lag} />}
          {selectedSeries?.type === 'indicator' && <FromIndicatorBadge />}
        </div>
      );
    }

    return (
      <div className="flex flex-wrap gap-4 items-center">
        {!hasSingleOption && (
          <label>
            Series:
            <select
              value={config.instrumentId}
              onChange={handleSeriesChange}
              className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
            >
              {allSeriesOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.label} {opt.type === 'indicator' ? ' (indicator)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {availableFields.length > 1 && (
          <label>
            Field:
            <select
              value={currentField}
              onChange={e => onChange({ ...config, field: e.target.value })}
              className="ml-2 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm"
            >
              {availableFields.map(f => (
                <option key={f} value={f}>
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                </option>
              ))}
            </select>
          </label>
        )}

        <label>
          Period:
          <input
            type="number"
            min="1"
            value={period}
            onChange={handlePeriodChange}
            className="ml-2 w-20 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
          />
        </label>
        <label>
          Lag:
          <input
            type="number"
            min="0"
            value={lag}
            onChange={handleLagChange}
            className="ml-2 w-20 px-2 py-1 border border-rule bg-newsprint-panel text-ink rounded-sm focus:outline-none focus:border-ink focus:ring-4 focus:ring-newsprint-dark"
          />
        </label>
      </div>
    );
  }
);

IndicatorEditorRSI.displayName = 'IndicatorEditorRSI';

export default IndicatorEditorRSI;